/**
 * Consumer Profile Page
 *
 * Four-tab settings surface that consolidates account profile, financial
 * profile, notification preferences, and Astor preferences. Each tab body
 * lives in its own component under `components/profile/` and is independently
 * data-bound. Tab state is URL-synced via `?tab=…` so links and refreshes
 * land on the right tab.
 *
 * @see /tmp/design-pass/borrower/specs/profile-and-astor-panel.md (Screen 1)
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BellIcon from '@mui/icons-material/Notifications';
import PaidIcon from '@mui/icons-material/Paid';
import PersonIcon from '@mui/icons-material/Person';
import { Box, GlassAlert, GlassCard, GlassTabs, PushNotificationPrompt, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { AstorPrefsTab, FinancialProfileTab, NotificationsTab, ProfileTab } from '../../components/profile';
import { usePushNotificationContext } from '../../contexts/PushNotificationContext';
import { useProfile } from '../../hooks';

type ProfileTabId = 'profile' | 'financial' | 'notifications' | 'astor';

const VALID_TABS: ProfileTabId[] = ['profile', 'financial', 'notifications', 'astor'];

export default function ProfilePage(): ReactElement {
  const { t } = useI18n();
  const { error: profileError } = useProfile();
  const push = usePushNotificationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: ProfileTabId = useMemo(() => {
    const raw = searchParams.get('tab') as ProfileTabId | null;
    return raw && VALID_TABS.includes(raw) ? raw : 'profile';
  }, [searchParams]);

  const handleTabChange = (next: ProfileTabId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  // PageTemplate-level icon swaps with the active tab so the header surface
  // tells the user where they are without us needing 4 different routes.
  const icon = useMemo(() => {
    switch (activeTab) {
      case 'financial':
        return PaidIcon;
      case 'notifications':
        return BellIcon;
      case 'astor':
        return AutoAwesomeIcon;
      default:
        return PersonIcon;
    }
  }, [activeTab]);

  if (profileError) {
    return (
      <PageTemplate
        variant="settings"
        titleKey="consumer.profile.pageTitle"
        descriptionKey="consumer.profile.pageSubtitle"
        icon={PersonIcon}
      >
        <GlassAlert severity="error" visible>
          {t('profile.loadFailed')}: {profileError.message}
        </GlassAlert>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      variant="settings"
      titleKey="consumer.profile.pageTitle"
      descriptionKey="consumer.profile.pageSubtitle"
      icon={icon}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {/* Browser push prompt — shown once per session; can be dismissed. */}
        <PushNotificationPrompt
          entityType="application"
          onEnable={async () => {
            await push.requestPermission();
          }}
          pushEnabled={push.permissionState === 'granted'}
        />

        <GlassCard sx={{ overflow: 'visible', p: 0 }}>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
            <GlassTabs
              tabs={[
                { value: 'profile', label: t('consumer.profile.tabs.profile') },
                { value: 'financial', label: t('consumer.profile.tabs.financial') },
                { value: 'notifications', label: t('consumer.profile.tabs.notifications') },
                { value: 'astor', label: t('consumer.profile.tabs.astor') },
              ]}
              value={activeTab}
              onChange={(_, value) => handleTabChange(value as ProfileTabId)}
              size="medium"
            />
          </Box>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {activeTab === 'profile' && <ProfileTab />}
            {activeTab === 'financial' && <FinancialProfileTab />}
            {activeTab === 'notifications' && <NotificationsTab />}
            {activeTab === 'astor' && <AstorPrefsTab />}
          </Box>
        </GlassCard>
      </Box>
    </PageTemplate>
  );
}
